import React from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, ChartEvent, ActiveElement, Chart } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { AnalyticsCustomerTypeData } from '../../types';
import ErrorBoundary from '../ErrorBoundary';

ChartJS.register(ArcElement, Tooltip, Legend);

interface AnalyticsCustomerTypeChartProps {
  customerTypeData: AnalyticsCustomerTypeData[];
  selectedCustomerType: string | null;
  setSelectedCustomerType: (customerType: string | null) => void;
  selectedCustomerName: string | null;
  setSelectedCustomerName: (customerName: string | null) => void;
  metric: 'sales' | 'orderCount' | 'itemCount';
}

const AnalyticsCustomerTypeChart: React.FC<AnalyticsCustomerTypeChartProps> = ({
  customerTypeData,
  selectedCustomerType,
  setSelectedCustomerType,
  selectedCustomerName,
  setSelectedCustomerName,
  metric,
}) => {
  const formatMetric = (value: number, metric: string) => {
    if (metric === 'sales') return `$${value.toFixed(2)}`;
    return value.toString();
  };

  const totalValue = customerTypeData.reduce((sum, d) => sum + (d.value || 0), 0);

  const pieChartData = {
    labels: selectedCustomerName
      ? customerTypeData.map((d) => d.customer_name || 'Unknown Customer')
      : selectedCustomerType
      ? customerTypeData.map((d) => d.customer_name || 'Unknown Customer')
      : customerTypeData.map((d) => d.customer_type || 'Retail'),
    datasets: [
      {
        data: customerTypeData.map((d) => d.value || 0),
        backgroundColor: [
          '#3B7DD8', // accent blue
          '#E07856', // terracotta
          '#4FA37C', // sage green
          '#E3B341', // golden
          '#8E6FC9', // soft violet
          '#56A6C9', // sky teal
          '#D8689B', // rose
          '#7A9E5F', // olive
          '#C98B56', // amber brown
          '#7B8794', // slate grey
        ],
        borderColor: '#ffffff',
        borderWidth: 2,
        hoverBorderColor: 'rgb(255, 255, 255)',
        hoverOffset: 30,
      },
    ],
  };

  // Two levels of drill-down: customer type -> the customers inside it -> a single customer.
  const handlePieClick = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
    if (elements.length === 0) return;
    const data = customerTypeData[elements[0].index];
    if (!data) return;
    if (!selectedCustomerType) {
      if (data.customer_type) setSelectedCustomerType(data.customer_type);
      return;
    }
    if (!selectedCustomerName && data.customer_name) {
      setSelectedCustomerName(data.customer_name);
    }
  };

  const resetPieChart = () => {
    if (selectedCustomerName) {
      setSelectedCustomerName(null);
    } else if (selectedCustomerType) {
      setSelectedCustomerType(null);
    }
  };

  // Clearing a customer returns you to the customers in that type; clearing the type
  // returns you to the customer types.
  const backLabel = selectedCustomerName ? 'Back to Customers' : 'Back to Customer Types';

  const title = selectedCustomerName
    ? `${metric === 'sales' ? 'Sales' : metric === 'orderCount' ? 'Orders' : 'Items'} by Customer`
    : selectedCustomerType
    ? `${metric === 'sales' ? 'Sales' : metric === 'orderCount' ? 'Orders' : 'Items'} by Customer`
    : `${metric === 'sales' ? 'Sales' : metric === 'orderCount' ? 'Orders' : 'Items'} by Customer Type`;

  return (
    <div className="analytics-card analytics-pie-card">
      <div className="analytics-card-header">
        <h3>{title}</h3>
        {(selectedCustomerType || selectedCustomerName) && (
          <button className="analytics-action-button" onClick={resetPieChart}>
            {backLabel}
          </button>
        )}
      </div>
      <ErrorBoundary>
        <div className="analytics-pie-container">
          {pieChartData.labels.length > 0 && customerTypeData.some((d) => d.value > 0) ? (
            <Doughnut
              data={pieChartData}
              options={{
                responsive: true,
                plugins: {
                  legend: {
                    position: 'bottom',
                    labels: {
                      font: {
                        family: '-apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif',
                        size: 14,
                      },
                      color: '#1f2a44',
                      padding: 20,
                    },
                  },
                  tooltip: {
                    backgroundColor: '#ffffff',
                    titleColor: '#1f2a44',
                    bodyColor: '#1f2a44',
                    borderColor: '#e5e7eb',
                    borderWidth: 1,
                    cornerRadius: 8,
                    padding: 12,
                    callbacks: {
                      label: (context) => {
                        const value = context.raw as number;
                        const percentage = totalValue ? ((value / totalValue) * 100).toFixed(1) : '0';
                        return `${context.label}: ${formatMetric(value, metric)} (${percentage}%)`;
                      },
                    },
                  },
                },
                onClick: handlePieClick,
                hover: {
                  mode: 'nearest',
                  intersect: true,
                },
              }}
            />
          ) : (
            <p>No customer type data available for the selected filters.</p>
          )}
        </div>
      </ErrorBoundary>
    </div>
  );
};

export default AnalyticsCustomerTypeChart;